import React from "react";

import { emitBannerClick } from "@catchoftheday/analytics";
import { Flex, ImageResponsive, Link } from "@catchoftheday/cg-components";
import { SpaceProps, useTheme } from "@catchoftheday/theme";

import {
  BannerByPageQuery,
  useBannerByPageQuery,
  useCustomerQuery,
} from "@common-vendors/generated/graphql";

export type BannerProps = {
  isMobile: boolean;
  pageType: "PRODUCT" | "SEARCH";
};

type Banners = NonNullable<BannerByPageQuery["bannerByPage"]>["banners"];

export const PageBanner = ({
  isMobile,
  pageType,
  ...props
}: BannerProps & SpaceProps) => {
  const { data } = useBannerByPageQuery({
    variables: { input: { page: pageType } },
    ssr: true,
  });
  const { data: customerData } = useCustomerQuery({ ssr: false });
  const { breakpoints } = useTheme();

  const isMigrationEligible =
    customerData?.customer?.membership?.eligibleForMigration;

  const banners = data?.bannerByPage?.banners.filter((banner) =>
    banner.platforms.some(
      (platform) => platform === "DESKTOP" || platform === "MOBILE"
    )
  );

  if (!banners || isMigrationEligible) {
    return null;
  }

  const desktopBanners = banners?.filter((banner) =>
    banner.platforms.some((platform) => platform === "DESKTOP")
  );
  const mobileBanners = banners?.filter((banner) =>
    banner.platforms.some((platform) => platform === "MOBILE")
  );

  const renderBanners = (banners: Banners) => (
    <>
      {banners &&
        banners.map((banner, index) => (
          <Flex
            key={banner.id}
            maxWidth="1340px"
            mb="30px"
            overflow="hidden"
            px="10px"
            onClick={() =>
              banner.image.url &&
              emitBannerClick({
                id: 2,
                name: banner.image.url.substring(
                  banner.image.url.lastIndexOf("/") + 1
                ),
                targetUrl: banner.image.url,
                position: index + 1,
              })
            }
            justifyContent="center"
            {...props}
          >
            <Link href={banner.url}>
              <ImageResponsive
                src={banner.image.url}
                alt={banner.image.altText || "promotional banner"}
                maxHeight="192px"
                maxWidth={breakpoints.xl}
                width="auto"
                height={{
                  xs: `calc((100vw - 20px) / ${
                    banner.image.width / banner.image.height
                  })`,
                  xl: `calc(${breakpoints.xl} / ${
                    banner.image.width / banner.image.height
                  })`,
                }}
                lazyLoad
                margin="0 auto"
              />
            </Link>
          </Flex>
        ))}
    </>
  );

  if (isMobile) {
    return renderBanners(mobileBanners);
  } else {
    return renderBanners(desktopBanners);
  }
};
